/** How dashboard metrics map back to readiness-check artifacts and agent inference. */

const RC = 'SAP Readiness Check report';
const AGENT = 'Analysis Agent (derived from readiness package)';

export const KPI_SOURCE_DEFAULTS = {
  score: `${RC} — Business Functions / customization scoring`,
  impacts: `${RC} — Activities & mandatory conversion impacts`,
  addons: `${RC} — Compatibility Scope Analysis workbook`,
  simplif: `${RC} — Relevant Simplification Items workbook`,
  custom_findings: `${RC} — Custom Code chapter (ATC findings)`,
  custom_redesign: `${RC} — Custom Code chapter (functional redesign count)`,
  custom_tech: `${RC} — Custom Code chapter (technical adoption count)`,
  custom: `${RC} — Custom Code chapter (objects in scope)`,
  hana: `${RC} — HANA sizing / Data Volume Management`,
  iface: `${RC} — Interface Impact Analysis workbooks (rollup)`,
  blockers: `${RC} — Blockers & prerequisite section`,
  ml: `${RC} — Finance / Material Ledger chapter`,
  finance: `${RC} — Finance data-quality section`,
  cvi: `${RC} — CVI / Business Partner conversion section`,
  fiori: `${RC} — Recommended Fiori Apps / App Availability`,
  downtime: `${RC} — Activities / downtime calculator`,
  next: AGENT,
  questions: AGENT,
  modules: `${RC} — Business Functions & named process areas`,
  interview: AGENT,
};

export const INTEGRATION_SOURCE_MAP = {
  IDoc: `${RC} — Interface Impact Analysis (IDOC)`,
  'Web services': `${RC} — Interface Impact Analysis (WEB)`,
  'RFC / BAPI': `${RC} — Interface Impact Analysis (RFC)`,
  'BW extractors': `${RC} — Interface Impact Analysis (BWE)`,
  OData: `${RC} — Interface Impact Analysis (OData)`,
  SLT: `${RC} — Interface Impact Analysis / SLT replication`,
  'Flat file': `${RC} — Interface Impact Analysis (flat file)`,
};

export const IDENTITY_SOURCE_MAP = {
  Customer: `${RC} — report cover / Technical Properties`,
  SID: `${RC} — report cover / system header`,
  Source: `${RC} — Technical Properties (source product version)`,
  'Target S/4': `${RC} — Activities / target release`,
  Database: `${RC} — Technical Properties (database)`,
  'OS / kernel': `${RC} — Technical Properties (OS & kernel)`,
  'Analysis date': `${RC} — report metadata`,
};

export const CUSTOM_CODE_TILE_SOURCES = {
  'Total ATC findings': `${RC} — Custom Code.xlsx (total findings)`,
  'Functional redesign': `${RC} — Custom Code chapter (functional manual)`,
  'Technical adoption': `${RC} — Custom Code chapter (technical adoption)`,
  'Quick-fix capable': `${RC} — Custom Code chapter (quick-fix eligible)`,
  'Tech. manual': `${RC} — Custom Code chapter (technical manual)`,
  'SYCM custom objects': `${RC} — Custom Code / SYCM object counts`,
  'SYCM modifications': `${RC} — Custom Code / SYCM modifications`,
  'Relevant SAP Notes': `${RC} — Custom Code (linked SAP Notes)`,
  'Objects with findings': `${RC} — Custom Code (objects in scope)`,
};

export const SIGNAL_SOURCE_HINTS = {
  finance_chapter: `${RC} — Finance chapter text`,
  simplification_relevant: `${RC} — Relevant Simplification Items`,
  business_function: `${RC} — Business Functions workbook`,
  process_discovery: `${RC} — Business Functions / process naming`,
  fiori: `${RC} — Fiori / App Availability`,
  add_on: `${RC} — Compatibility Scope Analysis`,
  other_explicit: `${RC} — explicit report narrative`,
};

/** Prefer explicit inventory `source` on a section object, else a static default. */
export function resolveSectionSource(section, fallback) {
  if (!section || typeof section !== 'object') return fallback;
  const explicit = section.source || section.rc_source || section.provenance;
  if (explicit && String(explicit).trim()) return String(explicit).trim();
  return fallback;
}

const SCORE_INPUT_META = {
  atc_findings: { label: 'ATC findings', maxPts: 40 },
  custom_code_objects: { label: 'Custom code objects (in scope / SYCM)', maxPts: 20 },
  functional_redesign: { label: 'Functional redesign findings', maxPts: 15 },
  simplification_items: { label: 'Simplification items mentioned', maxPts: 15 },
  incompatible_addons: { label: 'Incompatible add-ons', maxPts: 10 },
  interface_risk: { label: 'Interfaces unavailable or blocked', maxPts: 10 },
};

function parseScoreInputToken(token) {
  const raw = String(token || '').trim();
  const eq = raw.indexOf('=');
  if (eq === -1) return { key: raw, value: '' };
  return { key: raw.slice(0, eq).trim(), value: raw.slice(eq + 1).trim() };
}

/** Rich hover text for the customization score KPI — shows band, inputs, and rationale. */
export function customizationScoreTooltip(inventory) {
  const score = inventory?.customization_score;
  if (!score || typeof score !== 'object') {
    return KPI_SOURCE_DEFAULTS.score;
  }

  const lines = [
    `${RC} — relative customization index`,
    `${AGENT} combines RC complexity signals into a 0–100 score (not a module license count).`,
    '',
  ];

  if (score.score != null && score.score !== '') {
    const band =
      score.band && String(score.band).toLowerCase() !== 'unknown'
        ? ` · ${String(score.band).toUpperCase()} band`
        : '';
    lines.push(`Result: ${score.score} / 100${band}`);
  }

  lines.push('Bands: LOW < 35 · MEDIUM 35–65 · HIGH > 65');
  lines.push('');

  const inputs = Array.isArray(score.inputs_used) ? score.inputs_used : [];
  if (inputs.length) {
    lines.push('Weighted inputs in this run:');
    inputs.forEach((token) => {
      const { key, value } = parseScoreInputToken(token);
      const meta = SCORE_INPUT_META[key];
      if (meta) {
        lines.push(`• ${meta.label}: ${value} (max ${meta.maxPts} pts)`);
      } else {
        lines.push(`• ${rawOrToken(token)}`);
      }
    });
  } else {
    lines.push('Inputs: ATC volume, custom objects, redesign count, simplification load,');
    lines.push('incompatible add-ons, and interface risk — when present in the RC extract.');
  }

  const rationale = String(score.rationale || '').trim();
  if (rationale) {
    lines.push('');
    lines.push(rationale);
  }

  return lines.join('\n');
}

function rawOrToken(token) {
  return String(token || '').trim();
}

export function kpiSource(id, inventory) {
  const defaults = KPI_SOURCE_DEFAULTS[id] || `${RC} — extracted inventory`;
  switch (id) {
    case 'score':
      return customizationScoreTooltip(inventory);
    case 'addons':
      return resolveSectionSource(inventory?.addons, defaults);
    case 'simplif':
      return resolveSectionSource(inventory?.simplification, defaults);
    case 'custom_findings':
    case 'custom_redesign':
    case 'custom_tech':
    case 'custom':
      return resolveSectionSource(inventory?.custom_code, defaults);
    case 'hana':
      return resolveSectionSource(inventory?.sizing, defaults);
    case 'iface':
      return resolveSectionSource(inventory?.integration, defaults);
    case 'ml':
      return resolveSectionSource(inventory?.finance?.material_ledger, defaults);
    case 'finance':
      return resolveSectionSource(inventory?.finance, defaults);
    case 'cvi':
      return resolveSectionSource(inventory?.cvi, defaults);
    case 'fiori':
      return resolveSectionSource(inventory?.fiori, defaults);
    case 'downtime':
      return resolveSectionSource(inventory?.downtime, defaults);
    case 'workshop':
    case 'interview':
      return resolveSectionSource(inventory?.workshop, defaults);
    default:
      return defaults;
  }
}

export function integrationRowSource(label, obj) {
  const fallback = INTEGRATION_SOURCE_MAP[label] || `${RC} — Interface Impact Analysis`;
  return resolveSectionSource(obj, fallback);
}

export function functionalAreaSource(area) {
  if (area?.source && String(area.source).trim()) return String(area.source).trim();
  const signal = area?.signal ? SIGNAL_SOURCE_HINTS[area.signal] : null;
  if (signal) return signal;
  if (area?.evidence) return `${RC} — "${String(area.evidence).slice(0, 120)}${area.evidence.length > 120 ? '…' : ''}"`;
  return `${RC} — Business Functions / process evidence`;
}

export function impactSource(item) {
  if (item?.source && String(item.source).trim()) return String(item.source).trim();
  if (item?.evidence) {
    return `${RC} — Activities / impacts: "${String(item.evidence).slice(0, 120)}${item.evidence.length > 120 ? '…' : ''}"`;
  }
  return `${RC} — Activities & mandatory impacts`;
}

export function diagramNodeSource(node) {
  if (node?.source && String(node.source).trim()) return String(node.source).trim();
  if (node?.provenance && String(node.provenance).trim()) return String(node.provenance).trim();
  if (node?.subtitle && /readiness|RC|report/i.test(node.subtitle)) return String(node.subtitle);
  return 'Analysis Agent — inferred from readiness package (interfaces, RC narrative, or landscape inventory)';
}
